import { useState } from "react";

import type { CompanionAgentsResponse } from "../api/types";
import { formatDateTime } from "../lib/format";
import { CompanionCard } from "./CompanionCard";

interface CompanionSectionProps {
  summaryText: string;
  agents: CompanionAgentsResponse | undefined;
  isLoading: boolean;
  isRefreshing: boolean;
  errorMessage: string | null;
  refreshEnabled: boolean;
  refreshBusy: boolean;
  onRefresh: () => void;
}

function statusLine(
  agents: CompanionAgentsResponse | undefined,
  isLoading: boolean,
  isRefreshing: boolean,
  errorMessage: string | null,
): string {
  if (errorMessage) {
    return errorMessage;
  }
  if (isRefreshing) {
    return "Refreshing companion agents...";
  }
  if (isLoading) {
    return "Loading companion agents...";
  }
  if (agents?.generated_at) {
    return `${agents.items.length} stored agents updated ${formatDateTime(agents.generated_at)}`;
  }
  if (agents) {
    return "Waiting for the next scheduled companion refresh.";
  }
  return "";
}

export function CompanionSection({
  summaryText,
  agents,
  isLoading,
  isRefreshing,
  errorMessage,
  refreshEnabled,
  refreshBusy,
  onRefresh,
}: CompanionSectionProps) {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const items = agents?.items ?? [];
  const status = statusLine(agents, isLoading, isRefreshing, errorMessage);

  return (
    <div className="companion-section">
      <div className="companion-heading">
        <p className="panel-eyebrow">Companion Agents</p>
        <button
          className="mini-button"
          type="button"
          disabled={refreshBusy || !refreshEnabled}
          title={
            refreshEnabled
              ? "Refresh companion agents with the admin token"
              : "Set ADMIN_TOKEN to enable companion refresh"
          }
          onClick={onRefresh}
        >
          Refresh
        </button>
      </div>
      <div className="companion-summary">{summaryText}</div>
      <div className={`companion-status${errorMessage ? " is-error" : ""}`}>{status}</div>
      <div className="companion-list">
        {items.length === 0 ? (
          <p className="empty-state">
            No stored companion agent output yet. It will appear after the next scheduled companion
            refresh.
          </p>
        ) : (
          items.map((item) => (
            <CompanionCard
              key={item.key}
              item={item}
              isOpen={openKey === item.key}
              onToggle={() => setOpenKey((current) => (current === item.key ? null : item.key))}
              onClose={() => setOpenKey((current) => (current === item.key ? null : current))}
            />
          ))
        )}
      </div>
    </div>
  );
}
